import { Component, OnInit, OnDestroy } from '@angular/core';
import { BenutzerProfilFavorit, ObjektTyp } from 'app/core/services/api-client';
import { Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { IAppState } from 'app/modules/shared-area/root-state';
import { map } from 'rxjs/operators';
import { getFavoriten, isFavoritenLoading } from 'app/modules/shared-area/ducks/user-profile/store/user-profile.selectors';
import { loadFavoriten, removeFavorit } from 'app/modules/shared-area/ducks/user-profile/store/user-profile.actions';

interface FavoriteGroup {
  objektTyp: ObjektTyp;
  favorites: BenutzerProfilFavorit[];
}

@Component({
  selector: 'app-favoriten',
  templateUrl: './favoriten.component.html',
  styleUrls: ['./favoriten.component.scss']
})
export class FavoritenComponent implements OnInit, OnDestroy {
  public favoriteGroups$: Observable<FavoriteGroup[]>;
  public isLoading$: Observable<boolean>;
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<IAppState>
  ) {}

  ngOnInit() {
    // Load favoriten on initialization
    this.store.dispatch(loadFavoriten());
    
    this.favoriteGroups$ = this.store.pipe(
      select(getFavoriten),
      map(favorites => this.groupAndSortFavorites(favorites))
    );
    this.isLoading$ = this.store.pipe(select(isFavoritenLoading));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  removeFavorite(objektTyp: ObjektTyp, objektId: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.store.dispatch(removeFavorit({ objektTyp, objektId }));
  }

  /**
   * Group favorites by objektTyp and sort by objektId within each group
   */
  private groupAndSortFavorites(favorites: BenutzerProfilFavorit[]): FavoriteGroup[] {
    // Define the desired tab order
    const tabOrder = [ObjektTyp.Stoff, ObjektTyp.Zitat, ObjektTyp.Spezies, ObjektTyp.Lieferant];
    
    // Group by objektTyp
    const grouped = favorites.reduce((acc, fav) => {
      if (!acc[fav.objektTyp]) {
        acc[fav.objektTyp] = [];
      }
      acc[fav.objektTyp].push(fav);
      return acc;
    }, {} as Record<ObjektTyp, BenutzerProfilFavorit[]>);

    // Convert to array in the specified order, only including groups that have favorites
    return tabOrder
      .filter(objektTyp => grouped[objektTyp] && grouped[objektTyp].length > 0)
      .map(objektTyp => ({
        objektTyp,
        favorites: grouped[objektTyp].sort((a, b) => a.objektId - b.objektId)
      }));
  }

  /**
   * Get navigation URL for an object based on its type
   */
  objektUrl(favorit: BenutzerProfilFavorit): any[] {
    switch (favorit.objektTyp) {
      case ObjektTyp.Zitat:
        return ['/dossier/citation/', favorit.objektId];
      case ObjektTyp.Stoff:
        return ['/dossier/substance/', favorit.objektId];
      case ObjektTyp.Lieferant:
        return ['/dossier/supplier/', favorit.objektId];
      case ObjektTyp.Spezies:
        return ['/dossier/species/', favorit.objektId];
      default:
        return ['/home'];
    }
  }
}
